import {
  STARTING_SYSTEM_ID,
  bellyLand,
  freshHull,
  shelfEra,
  repairLine,
  getSystem,
  hullIntegrity,
  WARP_OVERDRIVE_CORE,
  cargoCapacity,
  clusterJumpRangeFor,
  computeShipPerformance,
  activeComponentTypes,
  jumpRangeFor,
  loseCarried,
  tutorialSpawnOffset,
  Rng,
  type TutorialStep,
  starterDesign,
  LESSONS,
  CARRIED_AT_RISK,
  isApproaching,
  laterTutorialStep,
  type CargoItem,
  type Lesson,
  type Wallet,
  type ShipGraph,
  type SystemId,
} from '@interspacial/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DockedView } from './render/DockedView';
import { FlightView, type Pose } from './render/FlightView';
import { ShipBuilder } from './render/ShipBuilder';
import { Starmap, focusSystem } from './render/Starmap';
import { NotSignedInError, fetchGameToken, needsRenewal } from './net/token';
import {
  RefusedError,
  cacheAccount,
  cachedAccount,
  claimRewards,
  fetchAccount,
  seeLesson,
  pushAccount,
  runAction,
} from './net/account';
import type { Alert } from './render/RadioAlert';
import { LessonCard } from './render/LessonCard';
import { connectToSystem, type RoomLink } from './net/room';
import { peerClock, watchPeers, type ChatLine } from './net/peers';
import {
  freshPlayerState,
  fromRecord,
  toRecord,
  type PlayerState,
} from './net/playerState';
import type { FlightState } from './flightState';
import { MainMenu } from './render/MainMenu';
import { GameMenu } from './render/GameMenu';
import { SHOW_KEY_HINTS } from './render/hudScale';
import { powerStyle, powerVisible, useReportedPower } from './render/SystemsPower';
import {
  FADE,
  MUSIC_STEPS,
  SLOW_FADE,
  musicVolume,
  playTrack,
  setMusicVolume,
} from './audio/music';
import { pinTarget } from './render/SearchPanel';
import { AdminView } from './render/AdminView';
import { tutorialStartFor } from './net/tutorialStart';
import { isAdmin, readIdentity, type PlayerIdentity } from './net/identity';
import { keepSessionAlive } from './net/session';

type AuthState =
  | { status: 'loading' }
  | { status: 'signedIn'; token: string }
  | { status: 'signedOut' }
  | { status: 'error'; message: string };

/**
 * `mainMenu` is where everyone starts.
 *
 * Kept in the same union as the rest rather than as a separate "has started"
 * flag, so there is exactly one thing that decides what is on screen. Two
 * pieces of state that both have to agree is how you end up with a menu over a
 * cockpit.
 */
/**
 * What the screen is showing.
 *
 * The chart is *not* one of these, and used to be. As a mode it replaced the
 * cockpit, which meant unmounting it — and everything the cockpit holds went
 * with it: which switches were thrown, where the throttle was, whether cruise
 * had the ship. Opening the map to check a route and coming back to a cold
 * console is not a chart, it is a penalty for consulting one.
 *
 * It is an overlay now — see `chart` — so the ship carries on flying underneath
 * while you read it, which is the entire point of a map in a cockpit.
 */
type Mode = 'mainMenu' | 'docked' | 'flight' | 'ship' | 'admin';

/**
 * Whether a tutorial value means "over".
 *
 * Two spellings, because they arrive from different places: `null` is what
 * abandoning writes, and `'done'` is what completing writes. Both mean there is
 * nothing left to teach.
 */
function finished(step: TutorialStep | null | undefined): boolean {
  return step === null || step === 'done';
}

export function App(): JSX.Element {
  const [auth, setAuth] = useState<AuthState>({ status: 'loading' });

  const [mode, setMode] = useState<Mode>('mainMenu');

  /**
   * Whether the menu overlay is up.
   *
   * The *menu*, not the main menu — see `Mode`. It can be summoned at any point,
   * over any view, and it does not pause: the ship is still flying and other
   * players are still in the room.
   */
  const [menuOpen, setMenuOpen] = useState(false);

  /**
   * Who is flying, read out of the handoff token.
   *
   * Display only — the signature is not checked here, and the game backend is
   * what decides whether the token means anything. See `readIdentity`.
   */
  const player: PlayerIdentity | null =
    auth.status === 'signedIn' ? readIdentity(auth.token) : null;

  /**
   * The current handoff token, or null when there is not one.
   *
   * Named separately because `auth.token` only exists on the signed-in variant,
   * and a dependency array is outside the narrowing that proves it does. It
   * changes whenever the token is renewed, which is what makes the effects
   * below pick the new one up.
   */
  const token = auth.status === 'signedIn' ? auth.token : null;

  const admin = isAdmin(player);

  /**
   * Keep the ChaosTree session from lapsing while the game is open.
   *
   * Every other page gets this from `base.js`, which the game does not load —
   * so without it a session died five minutes in. See `net/session.ts`.
   */
  useEffect(() => {
    if (auth.status !== 'signedIn') return;
    return keepSessionAlive((event) => {
      if (event === 'lost') setAuth({ status: 'signedOut' });
    });
  }, [auth.status]);

  /**
   * Everything this account owns.
   *
   * One record rather than a spread of independent pieces, because that is what
   * the `players` row will be — see `net/playerState.ts`. Null until somebody
   * has signed in: the game genuinely does not know whose ship to show before
   * then, and inventing one is how you end up with a shared test account.
   */
  const [state, setState] = useState<PlayerState | null>(null);

  /**
   * Whether the record on screen is the record on the server.
   *
   * `loading` is the moment before the first answer; `offline` is after a
   * request failed. They look different on purpose — one is a wait and the other
   * is a problem — and only the second one blocks play.
   */
  const [link, setLink] = useState<'loading' | 'live' | 'offline'>('loading');

  /*
   * Load the account.
   *
   * The cache is shown first if there is one, purely so a reload has something
   * to draw. It is replaced by whatever the server says a moment later, and the
   * server always wins: the copy in this browser is a picture of the record, not
   * a second copy of it.
   */
  useEffect(() => {
    if (auth.status !== 'signedIn' || !player) {
      setState(null);
      setLink('loading');
      return;
    }

    let cancelled = false;
    const cached = cachedAccount(player.uuid);
    if (cached) setState(fromRecord(cached));

    fetchAccount(auth.token)
      .then(({ record, pending }) => {
        if (cancelled) return;
        cacheAccount(record);
        setState(fromRecord(record));
        // What the menu offers. Reported by the server and never decided here,
        // so a client cannot talk itself into a reward.
        setPending(pending);
        setLink('live');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('[account] could not load', error);
        setLink('offline');
      });

    return () => {
      cancelled = true;
    };
  }, [auth.status, player?.uuid]);

  /*
   * Push it back, at most once a second and a half.
   *
   * Every wallet change, every dent and every item picked up moves `state`, and
   * a request each would be dozens a minute for a record that only has to be
   * right when the player next loads it. Debounced rather than saved on
   * particular events because the events are spread across a dozen components
   * and the ones nobody thought of are exactly the ones that would be lost.
   */
  useEffect(() => {
    if (auth.status !== 'signedIn' || !player || !state) return;

    /*
     * Not while the admin console is open.
     *
     * The console edits the database directly, and this pushes what the browser
     * believes — so a save landing after an edit *undoes* it with a record from
     * before. Resetting your own account was the case that showed it: the row
     * was deleted, the debounced save arrived, `readRecord` recreated the
     * account to serve the write, and the stale `onboarded: true` went straight
     * back on. The introduction then never ran, and the berth effect — reading
     * an onboarded player with no berth — put the ship at an ordinary station,
     * which is why a reset ended at a dock.
     *
     * `mode` is in the dependencies so opening the console also *cancels* a save
     * already counting down. Leaving it re-reads the account, and saving resumes
     * from whatever the console actually left behind.
     */
    if (mode === 'admin') return;

    /*
     * And never while the record is not the live one.
     *
     * `state` also holds the cached copy shown before the first answer arrives,
     * and whatever was on screen while a re-read is in flight. Writing either
     * back is how a reset came undone: the row was replaced, this pushed the
     * account it had a moment ago, and the introduction was marked finished
     * again a second and a half later.
     *
     * The gate on `admin` above is the same rule for the same reason; this is
     * the general form of it.
     */
    if (link !== 'live') return;

    const timer = window.setTimeout(() => {
      const record = toRecord(state, player);
      cacheAccount(record);

      pushAccount(auth.token, record)
        // Back on its feet. Saving used to be gated on this being 'live', so the
        // first failure stopped it permanently -- and because the handoff token
        // expired after two minutes and was never renewed, the first failure was
        // guaranteed. Everything after it was lost, which is why abandoning the
        // tutorial put people back at the start.
        .then(() => {
          setLink('live');

          // The server is now level with this record, so an action may read it
          // back without flushing first. Guarded on identity: anything that
          // moved while this was in the air is still unsaved.
          if (live.current.state === state) unsaved.current = false;
        })
        .catch((error: unknown) => {
          console.error('[account] could not save', error);
          setLink('offline');
        });
    }, 1500);

    return () => window.clearTimeout(timer);
  }, [auth.status, token, player?.uuid, state, mode, link]);

  /*
   * Keep the handoff token alive.
   *
   * It lasts two minutes and was fetched exactly once, on mount. Everything
   * authorised by it therefore worked for two minutes and then stopped: saving
   * 401'd and gave up, and joining a room came back as `jwt expired`, which the
   * realtime log showed plainly. Two accounts sitting next to each other could
   * not see one another because neither was in a room.
   *
   * Renewed on a timer rather than on a failure, so nothing has to fail first.
   * The chaostree *session* behind it is kept alive separately -- that is
   * `keepSessionAlive`, and this would be pointless without it.
   */
  useEffect(() => {
    if (auth.status !== 'signedIn') return;

    let cancelled = false;

    const renew = () => {
      if (cancelled || !needsRenewal(auth.token)) return;

      void fetchGameToken()
        .then((token) => {
          if (!cancelled) setAuth({ status: 'signedIn', token });
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          // A lapsed session is a real sign-out. Anything else is a bad moment
          // on the network, and the next tick will try again.
          if (error instanceof NotSignedInError) setAuth({ status: 'signedOut' });
          else console.warn('[auth] could not renew the game token:', error);
        });
    };

    // A tab that was asleep comes back with a token long dead, and an interval
    // that only fires every thirty seconds would leave it dead until it did.
    const onWake = () => {
      if (document.visibilityState === 'visible') renew();
    };

    renew();
    const timer = window.setInterval(renew, 30_000);
    document.addEventListener('visibilitychange', onWake);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onWake);
    };
  }, [auth.status, token]);

  /*
   * What is playing, by where the player is.
   *
   * Keyed on the mode alone, so a crossing does not disturb it: a jump never
   * leaves flight, which means this effect does not re-run while one is under
   * way and cannot fight `onJumpPhase` for the track. Arriving is the same —
   * the scene is rebuilt and the mode is not, so the return to ambience is the
   * phase handler's to do.
   *
   * The menu gets the flight ambience rather than silence, because what is
   * behind the menu *is* the galaxy — and it means pressing play does not
   * change the music, which is the correct amount of ceremony for opening a
   * door you were already looking through.
   *
   * Nothing will actually sound until the player has clicked something; the
   * browser refuses before a gesture and `playTrack` arranges the retry.
   */
  useEffect(() => {
    if (mode === 'docked' || mode === 'ship') playTrack('station', FADE);
    else if (mode === 'flight' || mode === 'mainMenu') playTrack('space', SLOW_FADE);
  }, [mode]);

  /**
   * How loud the soundtrack is.
   *
   * Mirrored into React state only so the control redraws — the truth is in
   * `music.ts`, which owns it and stores it, and which is read once here for
   * the initial value.
   */
  const [musicLevel, setMusicLevel] = useState(() => musicVolume());

  /** What the last refused action said, for whoever wants to show it. */
  const [refusal, setRefusal] = useState<string | null>(null);

  /**
   * Ask the server to do something.
   *
   * The record that comes back *is* the new state — nothing is applied
   * optimistically and nothing has to be unwound when the answer is no. Flight
   * is carried across because the server does not keep it and every action
   * happens at a station anyway.
   */
  /*
   * ── What is in hand, for the things that cannot wait for the timer ─────────
   *
   * Every action answers with the whole stored record and `act` takes it, which
   * is right — the server is the authority on what an action did. But the record
   * it answers with is the one *stored*, and this browser saves on a timer a
   * second and a half behind. So an action taken shortly after a local change
   * comes back carrying the state from before it, and applying that reply undoes
   * the change.
   *
   * This has been patched twice already, once for `onboarded` and once for
   * `tutorial` — both by keeping whichever side was further along, which works
   * only for fields that move one way. The ship editor is the case that has no
   * such direction: a whole session of it is local, and pressing done posts the
   * blueprint immediately. The reply carried the hull from before the session
   * and put it straight back, so the drawing was saved as the *plan* and the
   * ship itself reverted — which looks exactly like the editor doing nothing.
   *
   * The general fix is to stop answering from behind: push what is in hand
   * first, so the record the action reads is the record the player is looking
   * at. Only when there is something pending, so an action with nothing to flush
   * costs nothing.
   */
  const unsaved = useRef(false);

  /**
   * The current record, for callbacks that must not be rebuilt to see it.
   *
   * `act` is memoised on `auth` alone and is passed to a dozen components; making
   * it depend on `state` would rebuild it on every wallet change and every dent.
   */
  const live = useRef<{ state: PlayerState | null; player: PlayerIdentity | null }>({
    state: null,
    player: null,
  });

  /**
   * Whether the record in hand may be written back at all.
   *
   * The same two gates the debounced save keeps, for the same two reasons — a
   * record that is not live is a cache or a re-read in flight, and the admin
   * console edits the database directly. A flush is a save; it answers to the
   * same rules.
   */
  const savable = useRef(false);

  const flush = useCallback(async (): Promise<void> => {
    const { state: current, player: who } = live.current;
    if (auth.status !== 'signedIn' || !current || !who) return;

    const record = toRecord(current, who);
    cacheAccount(record);
    await pushAccount(auth.token, record);

    // Only if nothing moved while it was in the air. `state` is replaced rather
    // than mutated, so identity is the whole test — and clearing the flag over
    // the top of a change made mid-request would lose exactly the change the
    // flag exists to protect.
    if (live.current.state === current) unsaved.current = false;
  }, [auth]);

  const act = useCallback(
    async (
      path: string,
      body: Record<string, unknown> = {},
      /**
       * Take the reply exactly as it is, and throw local progress away.
       *
       * The merge below exists to protect progress the server has not been told
       * about yet, and it does that by assuming both fields only ever move one
       * way. **Reset is the one action where they move the other way**, and the
       * assumption quietly undid it: the server correctly answered with a new
       * account at `onboarded: false`, this kept the dead account's `true`, and
       * the introduction never ran. The berth effect then read "onboarded" and
       * assigned an ordinary station, which is why a reset also landed the
       * player somewhere instead of out in open space.
       *
       * So the exception is named rather than the rule weakened. Everything
       * else still gets to keep what it has finished.
       */
      startingOver = false,
    ): Promise<boolean> => {
      if (auth.status !== 'signedIn') return false;

      /*
       * Pushed before it is read back — except when starting over.
       *
       * A reset is the one action whose whole point is to discard what is in
       * hand, and flushing first would hand the server the dead account to
       * recreate. Same reasoning as the merge exemption below, and the same
       * flag says so.
       */
      if (!startingOver && unsaved.current && savable.current) {
        try {
          await flush();
        } catch (error) {
          // Not fatal. The action still runs; it may simply answer from behind,
          // which is where it always answered from before this existed.
          console.error('[account] could not flush before an action', error);
        }
      }

      try {
        const record = await runAction(auth.token, path, body);
        cacheAccount(record);
        setState((current) => ({
          ...fromRecord(record),
          flight: startingOver ? null : (current?.flight ?? null),
          /*
           * Progress the server has not been told about yet.
           *
           * Actions answer with the whole record, and the record they answer
           * with is the one *stored* -- while finishing the tutorial is written
           * locally and reaches the server on the debounced save a second and a
           * half later. Docking does both at once: it marks the introduction
           * complete and then calls `/berth`, whose reply still says the player
           * is on the first step. Replacing state with that reply undid the
           * completion, and the tutorial started over.
           *
           * Both of these only ever move one way -- an account does not become
           * un-onboarded, and an introduction that is over stays over -- so
           * keeping whichever side is further along is safe in a way that a
           * general "trust the client" would not be.
           */
          onboarded: startingOver
            ? record.onboarded
            : record.onboarded || (current?.onboarded ?? false),
          /*
           * Whichever side is further through, never backwards.
           *
           * The step moves in this browser and reaches the server on a debounced
           * save, so a reply routinely carries a `tutorial` from before it. This
           * used to take the reply whenever the local step was unfinished, which
           * meant docking — which sets `deliver` locally and calls `/berth` in
           * the same breath — was immediately put back to `land`. See
           * `laterTutorialStep`.
           */
          tutorial: startingOver
            ? record.tutorial
            : laterTutorialStep(current?.tutorial ?? null, record.tutorial),
        }));
        setRefusal(null);
        return true;
      } catch (error) {
        if (error instanceof RefusedError) {
          setRefusal(error.message);
          return false;
        }
        console.error(`[account] ${path} failed`, error);
        setLink('offline');
        return false;
      }
    },
    [auth, flush],
  );

  /** Patch the record. Every mutation below goes through this. */
  const patch = useCallback(
    (change: Partial<PlayerState> | ((current: PlayerState) => Partial<PlayerState>)) => {
      // Something the server has not been told about yet. Cleared by the save,
      // and consulted by `act` before it reads the record back.
      unsaved.current = true;

      setState((current) => {
        if (!current) return current;
        return { ...current, ...(typeof change === 'function' ? change(current) : change) };
      });
    },
    [],
  );

  /**
   * The pieces, unpacked.
   *
   * Signed out there is nothing to unpack, so these fall back to a throwaway
   * blank record — the menu is the only thing rendered in that case, and it
   * reads none of them.
   */
  const blank = useMemo(() => freshPlayerState(), []);
  const owned = state ?? blank;

  // Mirrored every render, so `act` can flush what is on screen without being
  // rebuilt whenever any of it changes. Assigned rather than in an effect: an
  // action taken in the same commit should see this render's record, not the
  // last one's.
  live.current = { state, player };
  savable.current = link === 'live' && mode !== 'admin';

  const seed = owned.seed;
  const systemId = owned.systemId;
  const design = owned.blueprint;
  const hull = owned.hull;
  const wallet = owned.wallet;
  const cargo = owned.cargo;

  const setSeed = (next: string) => patch({ seed: next });
  const setDesign = (next: ShipGraph) => patch({ blueprint: next });
  const setHull = (next: ShipGraph | null) => patch({ hull: next });
  const setWallet = (next: Wallet | ((current: Wallet) => Wallet)) =>
    patch((current) => ({
      wallet: typeof next === 'function' ? next(current.wallet) : next,
    }));
  const setCargo = (next: CargoItem[]) => patch({ cargo: next });

  /**
   * Where the ship is, while it is out there.
   *
   * A ref because the flight loop writes it sixty times a second and nothing
   * should re-render for that. It is mirrored into the saved record on exit
   * from flight, which is when it needs to survive — see `stow` below.
   */
  const flight = useRef<FlightState | null>(owned.flight);

  // Adopted whenever the account changes, so signing in picks up where that
  // account left off rather than wherever the previous one did.
  useEffect(() => {
    flight.current = state?.flight ?? null;
  }, [state?.version, player?.uuid]);

  /** Write the live flight position back into the record. */
  const stowFlight = useCallback(() => {
    patch({ flight: flight.current });
  }, [patch]);

  /**
   * Stow it whenever flight is left, whatever the reason.
   *
   * The loop writes into a ref, which survives the view unmounting but not a
   * refresh. Every exit — the chart, the editor, the menu — passes through a
   * mode change, so one effect covers all of them and none of the call sites
   * has to remember.
   */
  useEffect(() => {
    if (mode !== 'flight') stowFlight();
  }, [mode, stowFlight]);

  const berth = owned.berth;
  const setBerth = (next: { stationId: string; padId: string } | null) => patch({ berth: next });

  /*
   * Re-read the account on the way out of the admin console.
   *
   * The console edits the *database* — it can hand out money, move a ship, set
   * standing or delete the account outright — and it does none of that through
   * this component's state. So after using it, what is on screen is a picture
   * of an account that no longer exists.
   *
   * That is not merely stale, it is destructive: the debounced autosave writes
   * `onboarded`, `tutorial`, `systemId` and the hull back a second and a half
   * later, so a stale copy *undoes* the edit. Resetting your own account from
   * the console put the row back to a new player and then immediately re-marked
   * it onboarded from this browser, which is why the introduction did not run.
   *
   * Keyed on leaving rather than on any particular tool, because every tool has
   * the same problem and a list of the ones that matter is a list that will be
   * wrong the moment somebody adds a fourteenth.
   */
  const wasAdmin = useRef(false);

  useEffect(() => {
    const leaving = wasAdmin.current && mode !== 'admin';
    wasAdmin.current = mode === 'admin';

    if (!leaving || auth.status !== 'signedIn') return;

    // The local flight position belongs to whatever the account was before the
    // console touched it, and may now name a system the ship is not in.
    flight.current = null;

    // Not live until it has been read back. That stops the autosave writing the
    // pre-console account over the top of whatever the console just did, which
    // is a race it wins whenever the read takes longer than the debounce.
    setLink('loading');

    fetchAccount(auth.token)
      .then(({ record, pending }) => {
        cacheAccount(record);
        setState(fromRecord(record));
        setPending(pending);
        setLink('live');
      })
      .catch((error: unknown) => {
        console.error('[account] could not re-read after admin', error);
        setLink('offline');
      });
  }, [mode, auth]);

  /** Whether an introduction is already being asked for. See below. */
  const startingTutorial = useRef(false);

  /** The same, for the berth a berthless player is put at. */
  const claimingBerth = useRef(false);

  /**
   * A berth to start from.
   *
   * A first-time account gets a *chosen* one: a core system with a small pad,
   * seeded off the account so a replay is the same approach. Everyone else gets
   * the first pad in whatever system they are in, which is only reached after
   * jumping somewhere new.
   *
   * Assigned rather than computed on the fly so it lands in the saved record
   * and stays put — the tutorial talks you towards a particular station, and it
   * cannot be a different one each frame.
   */
  /*
   * ── Not while flying ──────────────────────────────────────────────────────
   *
   * Launching clears the berth, which made a ship leaving a pad indistinguishable
   * from an account that had never had one -- so this fired on every undock. For
   * a player still in the tutorial that meant being moved to the tutorial's
   * station, in the tutorial's system, with the introduction started again: a
   * full reset every time they took off.
   *
   * A flying player having no berth is the correct state, not a gap to fill.
   */
  useEffect(() => {
    if (!state || state.berth || !player || mode === 'flight') return;

    /*
     * Only ever from the live record.
     *
     * This decides whether an introduction starts and where a berthless ship is
     * put — both of which write to the server — and `state` is not always the
     * server's answer. It is the cached copy while the first request is in
     * flight, and the *previous* account while a re-read is running after a
     * reset. Acting on either is how a freshly reset account claimed a berth it
     * had been given before the reset, and then skipped the introduction because
     * it had one.
     */
    if (link !== 'live') return;

    if (!state.onboarded) {
      /*
       * Where the introduction is talking them towards.
       *
       * ── Read from the step, not from the hold ──────────────────────────────
       *
       * An earlier pass had the introduction's crate aboard from the start and
       * used its address as the destination. The crate has moved to the mission
       * board — see `tutorialBoard` — so there is nothing in the hold to read,
       * and this kept asking the server to start an introduction that was
       * already running. Every ask after the first was refused, the berth was
       * never assigned, and the account sat on the menu with nothing to fly to.
       *
       * So: no step means it has not begun, and any approach step means it has.
       * The *system* is stored by the server and comes back on every load; the
       * station within it is picked the same deterministic way every time, so a
       * reload lands on the same berth rather than rolling a new one.
       */
      if (state.tutorial === null) {
        // Guarded against being asked twice while the first ask is in the air.
        // StrictMode runs this effect twice in development, and the second ask
        // is refused — which the player would read as an error on a fresh
        // account.
        if (startingTutorial.current) return;

        const start = tutorialStartFor(state.seed);
        if (start) {
          startingTutorial.current = true;
          void act('/tutorial/start', {
            systemId: start.systemId,
            stationId: start.stationId,
            padId: start.padId,
          }).finally(() => {
            startingTutorial.current = false;
          });
          return;
        }
        // No candidate system. Vanishingly unlikely, and falling through to the
        // ordinary berth is a better answer than refusing to start.
      } else if (isApproaching(state.tutorial)) {
        const home = getSystem(state.seed, state.systemId);

        // The first berth a starter could use, which is the same rule
        // `tutorialCandidates` filtered on — and deterministic, so the station
        // Control names does not change when the page reloads.
        const station = home.stations.find((one) =>
          one.pads.some((pad) => pad.size === 'small'),
        );
        const pad = station?.pads.find((one) => one.size === 'small');

        if (station && pad) {
          patch({ berth: { stationId: station.id, padId: pad.id } });

          /*
           * Point the ship at the station it is about to be told to fly to.
           *
           * Control says "fly that thing over here" and, until now, every
           * instrument disagreed: the nav panel read the local star, the compass
           * pointed at it, and there was no bracket on the thing being discussed.
           * New players reported exactly that disconnect, and they were right —
           * the game was telling them one thing and showing them another.
           *
           * Pinned rather than merely highlighted, so the introduction uses the
           * same machinery every later destination does.
           */
          pinTarget({
            kind: 'body',
            label: station.name,
            detail: `your berth · ${home.name}`,
            systemId: state.systemId,
            clusterId: state.systemId.split('-')[0] as never,
            bodyName: station.name,
          });

          return;
        }
      }
    }

    /*
     * Somewhere to be, for a player who has no berth and is not flying.
     *
     * ── And the server has to be told ──────────────────────────────────────
     *
     * This used to `patch` alone, which set the berth in this browser and
     * nowhere else: `PUT /state` deliberately does not carry a berth — only
     * `/berth` writes one — so the client would draw a station, the player would
     * walk up to a counter, and every action refused with "you are not docked
     * anywhere". Which was true, on the only copy that decides.
     *
     * Patched *as well* rather than instead, so the view does not wait on a
     * round trip to know where it is. The reply carries the same berth back, and
     * `state.berth` then stops this effect running again.
     */
    const station = getSystem(state.seed, state.systemId).stations[0];
    if (!station || claimingBerth.current) return;

    const berth = { stationId: station.id, padId: station.pads[0]?.id ?? '' };

    claimingBerth.current = true;
    patch({ berth });
    void act('/berth', { ...berth, systemId: state.systemId }).finally(() => {
      claimingBerth.current = false;
    });
  }, [
    state?.seed,
    state?.systemId,
    state?.berth,
    state?.onboarded,
    // The step rather than the hold: the destination is read from where the
    // introduction has got to, and the hold no longer says anything about it.
    state?.tutorial,
    link,
    player?.uuid,
    mode,
    patch,
    act,
  ]);

  /**
   * A crossing in progress.
   *
   * Owned here rather than in the flight view because the arrival *is* a change
   * of system, and changing the system rebuilds that view's entire scene. The
   * state that decides when to do it therefore has to live above the thing it
   * tears down.
   *
   * `ready` is what the tunnel waits on past its minimum. Generating a system
   * is quick, but it is real work and it is honest to gate on having done it
   * rather than on a timer that assumes it.
   */
  const [crossing, setCrossing] = useState<{ systemId: SystemId; ready: boolean } | null>(null);

  /**
   * Whether the chart is up.
   *
   * Separate from `mode` so it sits *over* the cockpit or the berth rather than
   * replacing either. See the note on `Mode`.
   */
  const [chart, setChart] = useState(false);

  /*
   * Escape opens the menu, and closes it.
   *
   * ── What it has to give way to ─────────────────────────────────────────────
   *
   * The chart owns Escape while it is up — see `Starmap` — so this stands aside
   * for it rather than both firing and the player losing the map and gaining a
   * menu from one press.
   *
   * ── And what the browser takes first ───────────────────────────────────────
   *
   * While the cockpit holds pointer lock, Escape is swallowed by the browser to
   * release the lock and never reaches the page. So in flight the first press
   * frees the cursor and a second opens this. That is why the corner button
   * matters: with the stand-in cursor it is the one-step path, exactly like every
   * other control on the HUD.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      // Closing always wins, so the menu can never be the thing you are stuck
      // behind.
      if (menuOpen) {
        setMenuOpen(false);
        return;
      }

      if (chart) return;
      setMenuOpen(true);
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen, chart]);

  /**
   * Whether we got here by jumping.
   *
   * Decides where the flight view puts the ship when it next builds a scene:
   * beside the local star rather than on a pad. Cleared on docking, because
   * from then on the berth is where launching from means.
   */
  const [jumpedIn, setJumpedIn] = useState(false);

  /**
   * What coming back is worth, as the server last reported it.
   *
   * Read from `/state` and never worked out here. The client showing a figure it
   * calculated itself would be a figure it could be wrong about — and the one
   * place it would show is a promise to the player.
   *
   * Cleared the moment a claim is made rather than on the reply, so the offer
   * cannot be taken twice by pressing play twice. The server would refuse the
   * second anyway; this is so the *menu* stops promising.
   */
  const [pending, setPending] = useState({ daily: 0, weekly: 0 });

  /**
   * Something for whichever view is showing to say out loud.
   *
   * The radio belongs to the views — the cockpit and the berth each keep their
   * own log — so the app cannot push into one directly. This is the way in: a
   * ready-made `Alert` that whichever view is mounted folds into its queue when
   * the id changes.
   *
   * An `Alert` rather than a new type, because that is already exactly the shape
   * of "somebody is talking to you", and a reward landing is not different in
   * kind from a station hailing you.
   */
  const [notice, setNotice] = useState<Alert | null>(null);

  /** The lesson being shown loudly, if one is. */
  const [lesson, setLesson] = useState<Lesson | null>(null);

  /**
   * Whether the cockpit's instruments are lit, reported by the flight view.
   *
   * A lesson card is drawn here rather than in the cockpit — a lesson belongs to
   * the account, not to whichever view raised it — which meant it knew nothing
   * about a jump blacking the console out and sat lit over the top of one. It
   * reads as fully lit anywhere there is no cockpit to be dark. See
   * `SystemsPower`.
   */
  const cockpit = useReportedPower();

  /**
   * A feature has been touched. Teach it if this is the first time.
   *
   * The client notices; the server decides. `firstTime` comes back from a
   * guarded update rather than from this browser's copy of the record, so two
   * tabs raising the module at once produce one lesson rather than two — see
   * `seeLesson`.
   *
   * Fired without being awaited: the quiet half has already gone to the console
   * by the time this returns, and a card that appeared fifty milliseconds after
   * the thing it describes is indistinguishable from one that appeared with it.
   */
  /**
   * Lessons with a request already in the air.
   *
   * The record does not say a lesson has been seen until the answer lands, so
   * anything that can fire twice in that window — a scene rebuild re-arming its
   * triggers, a panel remounting — would ask again. The server refuses the
   * second one correctly, so this saves the round trip rather than a duplicate
   * card, which is still worth the four lines.
   */
  const asking = useRef(new Set<string>());

  const teach = useCallback(
    async (id: string) => {
      if (auth.status !== 'signedIn') return;
      if (!LESSONS[id]) return;

      // Already known to be taught. Saves a round trip on every later use, and
      // the server is still the one that decides when this is wrong.
      if (state?.lessons?.includes(id)) return;
      if (asking.current.has(id)) return;
      asking.current.add(id);

      try {
        const { record, firstTime } = await seeLesson(auth.token, id);
        setState((current) =>
          current ? { ...current, lessons: record.lessons, tips: record.tips } : fromRecord(record),
        );

        if (firstTime && record.tips) setLesson(LESSONS[id]!);
      } catch (error) {
        // A lesson that failed to be recorded is not worth interrupting anybody
        // over — it stays untaught and the next use offers it again.
        console.error('[lesson] could not record', error);
      } finally {
        asking.current.delete(id);
      }
    },
    [auth, state?.lessons],
  );

  /*
   * Carrying enough to be worth losing.
   *
   * Watched here rather than in either view because it is a fact about the
   * account: it becomes true by selling cargo at a counter, by handing in a
   * delivery, or by withdrawing at a vault, and no single screen sees all
   * three. `teach` is already idempotent and already asks the server, so a
   * balance that crosses the line, dips under and crosses again costs one round
   * trip in total.
   */
  useEffect(() => {
    if (!state) return;
    if (state.wallet.carried < CARRIED_AT_RISK) return;
    void teach('carried-vs-banked');
  }, [state?.wallet.carried, teach]);

  /**
   * Take the login reward, and say what actually arrived.
   *
   * Announced from the reply rather than from the offer: another client may have
   * collected it between the menu drawing and play being pressed, in which case
   * the server correctly pays nothing and there is nothing to announce. Telling
   * the player about linelits they did not receive would be worse than saying
   * nothing at all.
   */
  const collectRewards = useCallback(async () => {
    if (auth.status !== 'signedIn') return;

    try {
      const { record, granted } = await claimRewards(auth.token);
      cacheAccount(record);

      /*
       * Take the money and nothing else.
       *
       * This was `setState(fromRecord(record))`, which replaces the *whole*
       * record — and it is fired without being awaited while the play button is
       * already switching views, so it lands a round trip after the cockpit or
       * the berth has mounted and started writing state of its own. Everything
       * done in between was discarded.
       *
       * Worse, `fromRecord` sets `flight: null` and takes `onboarded` and
       * `tutorial` from what the *server* has stored — which lags the client by
       * up to the debounced save. `act()` has a long note about exactly this and
       * merges those two fields deliberately; this bypassed it, so collecting a
       * reward could throw away a flight position and restart a finished
       * introduction.
       *
       * A claim only ever moves money, so only money is applied. Functional, so
       * it merges into whatever the state is by the time the reply arrives
       * rather than into whatever it was when the request left.
       */
      setState((current) =>
        current
          ? { ...current, wallet: record.wallet, cargo: record.cargo }
          : fromRecord(record),
      );

      const total = granted.daily + granted.weekly;
      if (total <= 0) return;

      const both = granted.daily > 0 && granted.weekly > 0;
      setNotice({
        id: `reward-${Date.now()}`,
        from: 'Guild Ledger',
        source: 'automated transfer',
        text: both
          ? `Welcome back. ${granted.daily} daily and ${granted.weekly} weekly credited to your vaults — ${total} in total.`
          : `Welcome back. ${total} ${granted.weekly > 0 ? 'weekly' : 'daily'} credited to your vaults.`,
      });
    } catch (error) {
      // A reward that did not arrive is not worth interrupting anybody over. It
      // stays claimable — nothing was recorded — and the next launch picks it up.
      console.error('[account] could not claim rewards', error);
    }
  }, [auth]);

  /**
   * How far the ship in service can jump, both kinds.
   *
   * Off the hull rather than the blueprint wherever there is one: the chart
   * should plan for what is actually on the pad, dents and all.
   */
  const shipReach = useMemo(() => {
    /*
     * Only what is actually running.
     *
     * A thruster walled in behind a bulkhead, or one whose mounting wall has
     * been shot off, pushes against nothing — see `activeComponents`. The editor
     * has always drawn that rule and the flight loop used to ignore it, so a
     * ship flew at a speed its own yard said it could not reach.
     */
    const performance = computeShipPerformance(activeComponentTypes(hull ?? design));
    return { jump: jumpRangeFor(performance), cluster: clusterJumpRangeFor(performance) };
  }, [hull, design]);

  /*
   * The room for the system we are in.
   *
   * Joined while flying and left on a jump — one room per system, and the
   * crossing itself is spent in neither. Nothing depends on it: with no realtime
   * server the link sits at `unavailable` and the game plays exactly as it did
   * before there was one.
   */
  const [roomLink, setRoomLink] = useState<RoomLink>({ state: 'idle', room: null });

  /*
   * In the world, as opposed to in a menu.
   *
   * Docking used to leave the room. That made a station the one place in the
   * game where players were guaranteed not to see each other: a ship touching
   * down vanished from everyone else's sky, and the pilot inside it went blind
   * to the traffic outside. Both are wrong in the same place, and for the same
   * reason -- the room was scoped to the flight view rather than to the system.
   *
   * The ship builder counts too. Editing is something you do at a berth, not
   * somewhere else, and blinking out of existence because somebody opened a
   * panel would be the old bug with a smaller window.
   */
  const inWorld = mode === 'flight' || mode === 'docked' || mode === 'ship';

  useEffect(() => {
    if (auth.status !== 'signedIn' || !inWorld) {
      setRoomLink({ state: 'idle', room: null });
      return;
    }

    return connectToSystem({ token: auth.token, systemId, onChange: setRoomLink });
  }, [auth.status, inWorld, systemId]);

  /*
   * What our ship looks like, sent once rather than every tick.
   *
   * The graph only changes when a wall is destroyed, which is rare — so this
   * runs on joining a room and again when the hull actually differs, and the ten
   * poses a second carry nothing but numbers.
   *
   * `hull ?? design` is what is flown, and so what other people should see: a
   * ship that has lost half its nose should look like one.
   */
  const flown = hull ?? design;

  /*
   * What the hull *is*, as opposed to what condition it is in.
   *
   * `hull` is a fresh object on every dent, and damage ticks — a ship in a
   * corona takes it ten times a second — so an effect watching the graph itself
   * would re-broadcast every joint and line at that rate for a shape that had
   * not changed. This changes only when something is added or lost, which is
   * what "appearance changed" actually means.
   */
  const shape = useMemo(
    () =>
      [
        flown.joints.map((joint) => joint.id).join(','),
        flown.lines.map((line) => line.id).join(','),
        flown.components.map((part) => part.id).join(','),
      ].join('|'),
    [flown],
  );

  const flownRef = useRef(flown);
  flownRef.current = flown;

  useEffect(() => {
    const room = roomLink.room;
    if (!room) return;
    const flown = flownRef.current;

    room.send('profile', {
      joints: flown.joints.map((joint) => ({ id: joint.id, x: joint.x, y: joint.y })),
      lines: flown.lines.map((line) => ({
        id: line.id,
        a: line.a,
        b: line.b,
        health: line.health,
        strength: line.strength,
        length: line.length,
        color: line.color ?? '',
      })),
      components: flown.components.map((part) => ({
        id: part.id,
        type: part.type,
        x: part.position.x,
        y: part.position.y,
      })),
    });
  }, [roomLink.room, shape]);

  /*
   * Where we are, handed straight through to the room.
   *
   * A ref rather than a callback that changes with the link, because the flight
   * loop stores what it is given and a new function identity every render would
   * mean nothing here — but it also means a room that goes away mid-flight
   * simply stops receiving, which is the correct behaviour and needs no
   * handling.
   */
  const roomRef = useRef(roomLink.room);
  roomRef.current = roomLink.room;
  const publishPose = useCallback((pose: Pose) => {
    roomRef.current?.send('pose', pose);
  }, []);

  /*
   * Saying where you are parked, while parked.
   *
   * The flight loop publishes a pose ten times a second and does not exist while
   * docked, so this is the other half: which pad, repeated slowly, for as long
   * as the ship is sitting on it.
   *
   * No position goes with it. Stations orbit, so a ship that broadcast the
   * coordinates it landed at would sit where the station used to be and drift
   * further out for as long as it stayed. The pad is the durable answer, and
   * each receiver resolves it against the station in its own scene -- which is
   * moving the same way, and which is still right after a reload.
   *
   * Once a second rather than ten times: nothing about a parked ship changes,
   * and the repeat exists only so a player who joins later learns the pad is
   * taken. The first one goes out immediately, so somebody already watching sees
   * the ship land rather than a second later.
   */
  useEffect(() => {
    const room = roomLink.room;
    if (!room || mode === 'flight' || !berth) return;

    const announce = () => {
      room.send('pose', {
        docked: true,
        stationId: berth.stationId,
        padId: berth.padId,
        hidden: false,
      });
    };

    announce();
    const timer = window.setInterval(announce, 1000);
    return () => window.clearInterval(timer);
  }, [roomLink.room, mode, berth?.stationId, berth?.padId]);

  /*
   * What people are saying, and the one command that costs something.
   *
   * Kept here rather than in either view because both have a radio and a player
   * moving between them should not lose the conversation. Held to the last sixty
   * lines: a log nobody has read in an hour is not worth the memory, and the
   * panel only ever shows the end of it.
   */
  const [chat, setChat] = useState<readonly ChatLine[]>([]);

  useEffect(() => {
    const room = roomLink.room;
    if (!room) return;

    return room.onMessage('chat', (line: ChatLine) => {
      setChat((log) => [...log.slice(-59), line]);
    });
  }, [roomLink.room]);

  /**
   * Say something, or spend a beacon to shout it.
   *
   * `/alert <message>` reaches everybody in the system rather than only whoever
   * is reading the local channel, and costs a Radio Pulse Beacon out of the
   * hold. The beacon is taken first and the message sent only if that worked --
   * so a pilot with none aboard shouts nothing, and finds out why.
   */
  const say = useCallback(
    async (channel: string, text: string) => {
      const room = roomRef.current;
      const trimmed = text.trim();
      if (!trimmed) return;

      const alert = /^\/alert\s+/i.test(trimmed);
      const body = alert ? trimmed.replace(/^\/alert\s+/i, '').trim() : trimmed;
      if (!body) return;

      if (!room) {
        // Nobody to hear it. Said plainly rather than swallowed, because a chat
        // box that accepts a line and does nothing looks like a delivered one.
        setChat((log) => [
          ...log.slice(-59),
          { from: 'system', callsign: '', uuid: '', text: 'Not connected — nobody can hear you.', channel: 'local', alert: false, at: Date.now() },
        ]);
        return;
      }

      // The beacon goes first. If the hold is empty this refuses, `act` shows
      // the reason, and nothing is broadcast.
      if (alert && !(await act('/beacon'))) return;

      room.send('chat', { text: body, channel, alert });
    },
    [act],
  );

  /*
   * Everybody else in the system.
   *
   * Made from the room and handed to the flight view, which reads it every
   * frame. Deliberately not state that anything renders off — peers change ten
   * times a second each, and the HUD has nothing to say about them.
   *
   * A new room means a new feed: the listeners are attached to the room's state
   * and a fresh map is the correct starting point for a system nobody has been
   * seen in yet.
   */
  const peers = useMemo(
    () => (roomLink.room ? watchPeers(roomLink.room, peerClock) : null),
    [roomLink.room],
  );

  const capacity = cargoCapacity((hull ?? design).components);

  /**
   * Overdrive cores in the hold.
   *
   * Counted out of cargo rather than tracked separately: they are ordinary
   * freight that happens to be spendable, so they take up hold space, can be
   * sold, and can be lost with the ship like anything else.
   */
  const cores = cargo
    .filter((item) => item.id === WARP_OVERDRIVE_CORE.id)
    .reduce((total, item) => total + item.count, 0);
  /*
   * Against the blueprint, not against itself.
   *
   * Damage removes walls now, so a hull measured against its own lines reports a
   * higher number the more of it falls off — a ship down to one pristine wall
   * would read a hundred per cent.
   */
  const integrity = hull ? hullIntegrity(hull, design) : 1;

  /*
   * "Flying your own design" is gone, and with it the loaner.
   *
   * It compared the hull in service against the blueprint line by line to work
   * out whether the ship you were in was *yours* — which supposed there were
   * two kinds of ship, and there are not. There is one ship. `design` is what
   * it looks like undamaged and `hull` is the state it is in, and losing it
   * rebuilds the second from the first. Nothing needed a third idea in between.
   */

  /** Put a ship in service, whole. What undocking does, and what a rebuy is. */
  const commission = () => setHull(freshHull(design));

  /**
   * Put one wall back, leaving the rest of the hull alone.
   *
   * Commissions a whole hull when there is not one yet: a design that has never
   * flown has no damage to fix, and the editor should not have to know that.
   */
  const repairWall = (lineId: string) =>
    setHull(hull ? repairLine(hull, lineId) : freshHull(design));

  /**
   * Where a tutorial player is dropped, relative to their station.
   *
   * Only computed while the introduction is running — everyone else launches
   * from the pad they docked at. Random rather than seeded off the account, to
   * match the station choice: no two runs should start on the same bearing just
   * because they belong to the same person.
   *
   * `useMemo` rolls it once per mount, which is all the stability it needs —
   * the offset is read when the flight view builds its scene, and from the
   * first frame onward the ship's position is saved and restored like anyone
   * else's.
   */
  const tutorialSpawn = useMemo(() => {
    if (!player || !state || state.onboarded) return null;
    return tutorialSpawnOffset({ float: Math.random });
  }, [player?.uuid, state?.seed, state?.onboarded]);


  useEffect(() => {
    let cancelled = false;

    fetchGameToken()
      .then((token) => {
        if (!cancelled) setAuth({ status: 'signedIn', token });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof NotSignedInError) setAuth({ status: 'signedOut' });
        else setAuth({ status: 'error', message: (error as Error).message });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Where the play button goes.
   *
   * Straight back into the cockpit if there is a flight in progress, and to the
   * berth otherwise. Resuming into a chart or the editor would be technically
   * accurate and completely wrong — those are places you step out to, not
   * places you are.
   */
  /**
   * Retire the tutorial.
   *
   * Stable, because the docked view has it in an effect's dependencies — an
   * inline arrow there would re-fire the closing transmission on every render.
   * That exact mistake is what wedged the alert queue in flight.
   */
  const clearTutorial = useCallback(() => {
    patch({ tutorial: null });
  }, [patch]);

  /**
   * Whether the introduction is still to be flown.
   *
   * `onboarded` alone is not enough: it only turns true on docking, so a player
   * part-way through has both a running tutorial and an un-onboarded account,
   * and both have to send them to the cockpit rather than to a berth.
   *
   * But onboarded is enough to rule it *out*. It is set once, on the landing
   * that ends the introduction, and nothing sets it back — so an account that
   * carries it and a leftover step is an account whose tutorial was finished and
   * whose step did not save. Reading it this way costs nothing and repairs every
   * record already stuck that way, which is otherwise a database edit per
   * player.
   */
  const teaching =
    !owned.onboarded && owned.tutorial !== null && owned.tutorial !== 'done';

  /**
   * Where the play button goes.
   *
   * A first-time player starts *in flight*, a thousand units off a station, and
   * is talked in. Sending them to a berth instead was the bug that hid the
   * tutorial entirely: they would arrive already docked, with nothing left for
   * Control to talk them through, and no sign anything had been skipped.
   */
  /*
   * ── And a player with no berth has nowhere to be but the cockpit ──────────
   *
   * Docked is the default because it is where most sessions end. It cannot be
   * the default for a player who has no berth: `DockedView` draws the station
   * you are docked at, and with no station it draws nothing — a grey view with
   * the instruments still over it, and nothing thrown to say why.
   *
   * That is not a rare state. Three in ten systems have no station at all, so
   * logging off in one leaves no berth to assign — see the effect that assigns
   * them — and the account is then unable to resume at all. The comment above
   * that effect already says it: a player with no berth is in space, and that is
   * correct rather than a gap. This is that sentence applied here.
   */
  const resume: Mode = flight.current || teaching || !berth ? 'flight' : 'docked';

  /**
   * Where the ship was left.
   *
   * Read straight off the saved flight state, which already records the system
   * and whatever had hold of the ship — see `flightState.ts`. Docked, the
   * berth's own station is the answer.
   */
  const lastSeen = (() => {
    if (teaching && !flight.current) return null;

    const system = getSystem(seed, systemId);

    if (flight.current) {
      return { system: system.name, holding: flight.current.anchor };
    }

    /*
     * The system is always the answer to "where", and the berth only ever
     * refines it.
     *
     * This used to give up and say nothing at all when there was no berth, which
     * is the one case where the player most needs telling: log off in a system
     * with no station — three in ten of them — and the menu said you were
     * somewhere, without saying where. The system id was known the whole time.
     *
     * `holding` is what has hold of the ship, so null is the honest answer for a
     * ship nothing is holding, and the menu already leaves the clause off.
     */
    const station = berth ? system.stations.find((s) => s.id === berth.stationId) : undefined;
    return { system: system.name, holding: station ? `${station.name}, docked` : null };
  })();

  const blocked =
    auth.status === 'loading'
      ? 'Signing in…'
      : auth.status === 'signedOut'
        ? 'Sign in to ChaosTree to play. Your ship, cargo and linelits are tied to your account.'
        : auth.status === 'error'
          ? `Could not sign in: ${auth.message}`
          : null;

  return (
    <>
      {/* What the server just said no to.
          `refusal` has been set on every declined action since actions existed
          and was rendered nowhere, so a 409 — "that job is no longer on the
          board", "no room", "your vault is full" — reached the player as a red
          line in a console they do not have open. The sentence is written for
          them; this is where they read it. */}
      {refusal && <Refusal text={refusal} onDone={() => setRefusal(null)} />}

      {/* The menu. Above every view because it can be summoned over any of
          them, and outside the mode switch for the same reason. */}
      {menuOpen && (
        <GameMenu
          onClose={() => setMenuOpen(false)}
          onMainMenu={
            mode === 'mainMenu'
              ? undefined
              : () => {
                  // The live position only lives in a ref, so it has to be
                  // written back before anything unmounts the flight view.
                  stowFlight();
                  setMenuOpen(false);
                  setMode('mainMenu');
                }
          }
          music={musicLevel}
          musicSteps={MUSIC_STEPS}
          onSetMusic={(level) => {
            setMusicVolume(level);
            setMusicLevel(level);
          }}
          tips={state?.tips ?? true}
          onToggleTips={(on) => void act('/tips', { on })}
        />
      )}

      {/* The galaxy, behind the menu. The real map rather than a picture of one
          — it is what you are about to fly around in, and a still would be a
          second thing to keep in step for no gain. */}
      {mode === 'mainMenu' && (
        <>
          <Starmap
            seed={seed}
            currentSystemId={systemId}
            ship={flight.current}
          // And the live one, so the marker follows a ship that is still under
          // way while the chart is open over it.
          shipRef={flight}
            asBackdrop
            onSeedChange={setSeed}
            onEnterSystem={() => undefined}
          />
          <MainMenu
            player={player}
            blocked={blocked}
            resumeLabel={
              teaching && !flight.current
                ? 'begin'
                : resume === 'flight'
                  ? 'resume flight'
                  : 'at the berth'
            }
            lastSeen={lastSeen}
            pending={pending}
            onOpenMenu={() => setMenuOpen(true)}
            admin={admin}
            onPlay={() => {
              // Launching straight into the tutorial skips the berth, and with
              // it the undock that normally puts a hull in service.
              if (resume === 'flight' && !hull) patch({ hull: freshHull(design) });

              /*
               * Collect whatever was waiting, on the way in.
               *
               * Not awaited: a reward is a nice thing that happened, and making
               * somebody watch a spinner to receive ten linelits would be a
               * worse trade than occasionally seeing them arrive a moment after
               * the cockpit does. It cannot be claimed twice however this goes —
               * the guarantee is a UNIQUE in the database, not this call.
               *
               * The offer is cleared first, so pressing play twice in quick
               * succession does not show the promise a second time.
               */
              if (pending.daily + pending.weekly > 0) {
                setPending({ daily: 0, weekly: 0 });
                void collectRewards();
              }

              setMode(resume);
            }}
            onOpenAdmin={() => setMode('admin')}
          />
        </>
      )}

      {mode === 'docked' && berth && (
        <DockedView
          peers={peers}
          chat={chat}
          onChat={say}
          uuid={player?.uuid}
          seed={seed}
          systemId={systemId}
          stationId={berth.stationId}
          padId={berth.padId}
          design={design}
          hull={hull}
          integrity={integrity}
          wallet={wallet}
          onWalletChange={setWallet}
          cargo={cargo}
          onCargoChange={setCargo}
          capacity={capacity}
          notice={notice}
          onRepair={commission}
          onUndock={() => {
            // Cleared in both places, so launching starts on the pad rather
            // than wherever the last flight ended.
            flight.current = null;

            // Leaving a berth is the server's record to change too, or a
            // reload would put the ship back on a plate it has left.
            void act('/berth', { stationId: null });

            patch({
              flight: null,
              // A ship that has never flown, or one that was lost, is built
              // here. Anything else keeps the condition it came back in.
              ...(hull ? {} : { hull: freshHull(design) }),
            });
            setMode('flight');
          }}
          onOpenStarmap={() => setChart(true)}
          onOpenShipBuilder={() => setMode('ship')}
          onOpenCargo={() => setMode('ship')}
          shelfTaken={state?.shelves.taken ?? {}}
          act={act}
          onFeatureUsed={teach}
          unlocked={state?.unlocked}
          tutorial={owned.tutorial}
          callsign={player?.displayID ?? 'pilot'}
          // Cleared once said. `onboarded` is already true by now — this only
          // stops the welcome being read out again every time the player docks
          // here for the rest of the account's life.
          onTutorialDelivered={clearTutorial}
        />
      )}
      {/* A berth is where a flight *starts*, not a condition for one happening.
          Three in ten systems have no station, and jumping into one of those
          used to leave nothing rendered at all.

          That was first patched by naming the cases where flying is allowed — a
          berth to launch from, a jump that just landed, or a flight already
          saved here — and the list turned out to be incomplete in exactly the
          way a list of cases usually is. Log off in a station-less system and
          you have none of the three: no berth, no jump this session, and a saved
          flight that gets cleared on the way out. The account could not resume,
          and the failure was a grey screen rather than an error.

          The honest condition was never about the berth. It is whether we know
          which system the ship is in, and we always do — `systemId` is on the
          saved record. `FlightView` has handled a berthless start all along: no
          berth means the star is the origin, which is where an arrival is
          measured from anyway. */}
      {mode === 'flight' && (
        <FlightView
          seed={seed}
          systemId={systemId}
          stationId={berth?.stationId ?? ''}
          padId={berth?.padId ?? ''}
          design={hull ?? design}
          blueprint={design}
          onDamage={setHull}
          onPose={publishPose}
          peers={peers}
          chat={chat}
          onChat={say}
          uuid={player?.uuid}
          tutorial={owned.tutorial}
          callsign={player?.displayID ?? 'pilot'}
          spawnOffset={tutorialSpawn}
          cores={cores}
          cargo={cargo}
          onCargoChange={setCargo}
          capacity={capacity}
          notice={notice}
          /*
           * A jump puts you beside the star — and so does having nowhere else
           * to be put.
           *
           * With no pad to launch from, `FlightView` leaves the ship on the
           * origin of the system, and the origin is the *centre of the star*.
           * That is fine as a measuring point and uninhabitable as a spawn: it
           * puts the camera inside the sun, where backface culling means there
           * is nothing to see in any direction. The symptom is an empty void
           * with working instruments over it, which is not obviously a position
           * problem at all.
           *
           * A berthless, flightless resume is an arrival in every sense that
           * matters, so it takes the arrival that already exists: ninety
           * thousand units out on a bearing picked to miss everything, facing
           * the star. `restored` still wins inside `FlightView`, so this cannot
           * displace a player whose flight state did survive.
           */
          arriveAtStar={jumpedIn || (!berth && !flight.current)}
          jumpReady={crossing?.ready ?? false}
          onOpenStarmap={() => setChart(true)}
          // Flight keeps running underneath the chart, but its controls do not
          // — see `suspended`.
          suspended={chart}
          /*
           * The swap, done while the view is dark rather than when it clears.
           *
           * This tears down and rebuilds the entire flight scene, which is the
           * expensive part of a jump and the one thing that must not happen
           * against the arrival animation. Part-way into the crossing there are
           * thirteen seconds left to absorb it and nothing on screen but
           * streaks.
           *
           * The berth is assigned here rather than left to the effect that
           * normally does it: that effect runs *after* the render in which the
           * berth is null, and the cockpit only mounts when there is one — so a
           * null berth would unmount the view mid-crossing. Three in ten systems
           * have no station at all, which the flight view now handles; this
           * avoids a flicker in the other seven.
           */
          onJumpSwap={(target) => {
            const landing = getSystem(seed, target as SystemId).stations[0];

            flight.current = null;
            patch({
              systemId: target as SystemId,
              berth: landing ? { stationId: landing.id, padId: landing.pads[0]?.id ?? '' } : null,
              flight: null,
            });

            /*
             * Nothing is told to the server here, and that is deliberate.
             *
             * A `/berth` call sat here briefly and caused the worst bug of the
             * session: its reply carries the *stored* record, whose `systemId`
             * is still the system being left — the new one has only been patched
             * locally and reaches the server on the debounced save. `act`
             * replaces state with that reply, so the ship snapped back and
             * arrived a second time in the system it had just left.
             *
             * The stale berth it was trying to clear is handled where it cannot
             * race: `saveState` drops a berth that names a station the ship is no
             * longer anywhere near. See `saveState`.
             */

            /*
             * Nothing to do here any more.
             *
             * The chart used to store a journey's origin, so arriving had to
             * drag it along — otherwise the route drew the leg you had already
             * flown, from a system you had left. Routes are planned from where
             * the ship is at the moment of planning now, so the arrival is not
             * something navigation has to be told about.
             */

            setJumpedIn(true);
            setCrossing({ systemId: target as SystemId, ready: true });
          }}
          onJumpPhase={(phase) => {
            // The crossing is over and the run in has the ship. Nothing here
            // moves it — that happened at the swap — so this is bookkeeping.
            if (phase === 'idle') setCrossing(null);

            /*
             * The soundtrack follows the crossing, and this is the right place
             * for it rather than the flight loop: a jump rebuilds that whole
             * scene, and the music has to be the one thing that does not notice.
             *
             * Three different transitions, deliberately not all the same shape:
             *
             *   locked  — a crossfade. The ship has committed and the mood
             *             changes with it, but nothing has happened yet.
             *   tunnel  — a hard cut. The crossing is a discontinuity in the
             *             fiction and softening it with a fade is exactly the
             *             wrong instinct; this is the one moment that should
             *             land as a moment.
             *   idle    — a long fade back, so the ambience returns while the
             *             ship is still shedding the run-in speed rather than
             *             arriving before it has stopped.
             */
            if (phase === 'running') playTrack('jumpLocked', FADE);
            else if (phase === 'tunnel') playTrack('jumpTunnel', 0);
            else if (phase === 'idle') playTrack('space', SLOW_FADE);
          }}
          onTutorialStep={(step) => {
            /*
             * Flew off. Retired rather than paused: the account is marked
             * onboarded so Control never opens with the hail again, because
             * somebody who left the introduction eight thousand units behind
             * has answered the question of whether they want it. Replaying it
             * is a deliberate act, and there is a button for that in admin.
             */
            if (step === 'abandoned') {
              patch({ tutorial: null, onboarded: true });
              return;
            }

            // Landing is what finishes it, and `onDock` fires first — so the
            // last step arrives with the ship already berthed and simply needs
            // recording.
            patch({ tutorial: step });
          }}
          state={flight}
          onDock={({ fee, belly, ...arrived }) => {
            flight.current = null;
            setJumpedIn(false);

            /*
             * The berth, and the fee that comes with it, are the server's.
             *
             * It charges what an oversize plate costs and writes the ledger row;
             * the fee sent here is what was *shown*, and it is not what is read.
             */
            void act('/berth', {
              stationId: arrived.stationId,
              padId: arrived.padId,
              systemId,
              fee,
            });

            patch((current) => ({
              berth: arrived,
              flight: null,
              /*
               * Coming down on the hull, because there was no gear to come down
               * on. Five per cent off every wall, and never fatal — a landing
               * that could finish the ship is not an emergency option.
               *
               * Applied locally and carried up on the next `PUT /state`, which
               * is where hull damage lives: it only ever goes down, so the
               * server takes it as read.
               */
              ...(belly && current.hull ? { hull: bellyLand(current.hull) } : {}),
              /*
               * Setting down hands the introduction to the mission board.
               *
               * `onboarded` still goes true here rather than on the step, so a
               * player who quits during the approach gets the introduction
               * again — they have not been taught anything yet. What changed is
               * that landing is no longer the *end*: the step becomes `deliver`
               * and stays there until the crate is handed in, which is what the
               * board is gated on and what the server clears.
               */
              ...(current.tutorial && !finished(current.tutorial) && current.tutorial !== 'deliver'
                ? { tutorial: 'deliver' as TutorialStep, onboarded: true }
                : {}),
            }));
            setMode('docked');
          }}
          onHullLost={(towedTo) => {
            flight.current = null;

            /*
             * The ship is gone, and the server is the one that says what is left.
             *
             * Everything aboard went with it — carried linelits, the hold, the
             * hull *and the design*. What survives is the vault. The blueprint
             * going too is deliberate: banked linelits outlive a loss, so a
             * player who kept their drawing could buy straight back into a large
             * hull on the first edit, and the vault would be a refund rather
             * than a head start.
             */
            void act('/wreck', { stationId: towedTo ?? null, systemId });
            patch({ flight: null });
            // Towed to a berth, so that is where the game continues from.
            if (towedTo) setMode('docked');
          }}
          onFeatureUsed={teach}
          /*
           * Going back for something already seen, or never seen because the
           * loud half is off. Straight to the card — no round trip, because
           * nothing is being decided: the player asked.
           */
          onOpenLesson={(id) => {
            const found = LESSONS[id];
            if (found) setLesson(found);
          }}
          onResetAccount={() => {
            /*
             * Everything local goes with it, not just the record.
             *
             * The server deletes the row and builds a fresh account in its place,
             * and the reply is that account — but a reset that only swapped the
             * record would leave this browser holding the dead one's flight
             * position, its cached copy, and a pending reward offer belonging to
             * somebody who no longer exists.
             *
             * Back to the menu rather than into the cockpit: the new account has
             * not been onboarded, so the menu is where the introduction starts
             * from, and dropping straight into flight would skip the very thing
             * being asked for.
             */
            flight.current = null;
            setPending({ daily: 0, weekly: 0 });
            setNotice(null);

            /*
             * Reset, then *read the account back* rather than trusting the
             * reply.
             *
             * The reply is correct — the server deletes the row and rebuilds it
             * in one transaction — but it arrives into a browser that is still
             * holding the dead account, and everything else here has an opinion
             * about that: the autosave, the berth assignment, the cache. Going
             * back through the ordinary load path means a reset account starts
             * from exactly the state a fresh one does, by the same code, rather
             * than from a reply pasted over what was there.
             *
             * It is also what makes the reset *observable*: `link` says loading
             * until the read lands, and nothing writes or decides while it does.
             */
            void act('/reset', {}, true).then((ok) => {
              if (!ok || auth.status !== 'signedIn') return;

              setLink('loading');

              fetchAccount(auth.token)
                .then(({ record, pending }) => {
                  cacheAccount(record);
                  setState(fromRecord(record));
                  setPending(pending);
                  setLink('live');
                  setMode('mainMenu');
                })
                .catch((error: unknown) => {
                  console.error('[account] could not re-read after a reset', error);
                  setLink('offline');
                  setMode('mainMenu');
                });
            });
          }}
        />
      )}
      {/* A feature explained, the first time it is touched.
          Outside the mode switch: a lesson belongs to the account rather than to
          whichever view happened to raise it, and one fired on the way into a
          berth should survive arriving. */}
      {lesson && powerVisible(cockpit) && (
        <LessonCard
          power={powerStyle(cockpit)}
          lesson={lesson}
          onDismiss={() => setLesson(null)}
          onDisableTips={() => {
            setLesson(null);
            void act('/tips', { on: false });
          }}
        />
      )}

      {/* Admin. Gated on the role rather than hidden, because a debug tool you
          cannot find is not a debug tool — and gated again on the server, which
          is the only gate that counts: the role read here comes from a token
          nobody verified. */}
      {mode === 'admin' && admin && player && auth.status === 'signedIn' && (
        <AdminView
          uuid={player.uuid}
          token={auth.token}
          seed={seed}
          onExit={() => setMode('mainMenu')}
          onReloadSelf={() => {
            if (auth.status !== 'signedIn') return;
            void fetchAccount(auth.token).then(({ record, pending }) => {
              cacheAccount(record);
              setState(fromRecord(record));
              setPending(pending);
            });
          }}
        />
      )}

      {/* Over whatever is underneath, rather than instead of it. The cockpit
          keeps flying; the berth keeps its panels. */}
      {chart && mode !== 'mainMenu' && (
        <Starmap
          seed={seed}
          currentSystemId={systemId}
          ship={flight.current}
          // Where we are when we are not out flying. Resolved to a name because
          // that is what the chart's bodies are keyed by.
          dockedAt={
            berth
              ? (getSystem(seed, systemId).stations.find((s) => s.id === berth.stationId)?.name ??
                null)
              : null
          }
          // Always a way out now, because there is always something behind it.
          onClose={() => setChart(false)}
          // Off the hull in service, falling back to the blueprint before the
          // first launch — the chart should plan for the ship you have.
          jumpRange={shipReach.jump}
          clusterRange={shipReach.cluster}
          onSeedChange={setSeed}
          onEnterSystem={(id) => {
            // A different system is a different set of berths, so the old one
            // cannot be kept — the effect above assigns a new one.
            patch({ systemId: id as SystemId, berth: null, flight: null });
            flight.current = null;
            setChart(false);
            setMode('docked');
          }}
        />
      )}
      {mode === 'ship' && berth && (
        <ShipBuilder
          admin={isAdmin(player)}
          seed={seed}
          systemId={systemId}
          stationId={berth.stationId}
          padId={berth.padId}
          /* The ship you own, holes and all — the editor works on the real
             thing. The blueprint rides alongside as the template, so whatever
             came off is already showing as a ghost to build back. */
          design={hull ?? design}
          blueprint={design}
          wallet={wallet}
          onWalletChange={setWallet}
          cargo={cargo}
          onCargoChange={setCargo}
          capacity={capacity}
          onRepair={commission}
          onRepairLine={repairWall}
          taken={state?.shelves.taken ?? {}}
          act={act}
          onChange={setHull}
          onExit={() => {
            /*
             * Two saves, and they are different things.
             *
             * `/build` is the session itself: the hull as it now stands, and what
             * it cost out of the hold and the purse. It has to be an action
             * because `PUT /state` will not carry a hull that gained a wall — it
             * accepts damage and nothing else, which is right for the flight loop
             * and is why the editor used to appear to do nothing at all.
             *
             * `/blueprint` is the *plan*: this shape, undented, for a repair to
             * build back to later. Saved on the way out rather than on every
             * edit, so a half-finished change is not something a repair would
             * rebuild to. The server strips the health for itself.
             *
             * In this order, and awaited between: the blueprint reply carries the
             * stored hull, so sending it before the build would answer with the
             * hull from before the session and put it straight back.
             */
            void (async () => {
              if (hull) {
                await act('/build', { hull, cargo, wallet });
                await act('/blueprint', { blueprint: hull });
              }
              setMode('docked');
            })();
          }}
        />
      )}

      {mode !== 'mainMenu' && (
      <div
        style={{
          ...switcherStyle,
          /*
           * Almost gone while the ship is dark, rather than gone.
           *
           * Everything else on screen switches off for a jump — that is the
           * whole effect, and a lit menu button sitting in the corner through it
           * was the one thing insisting there was a browser here. But it is also
           * the only way out of a view, so it cannot actually leave: at a tenth
           * it is still there for a hand that goes looking, and no longer part of
           * what the eye reads.
           *
           * Faded rather than collapsed, deliberately. The tube effect is for
           * the ship's instruments; this is not one of them, and having it
           * pretend to be would claim the jump had switched off something it
           * does not own.
           */
          opacity: powerVisible(cockpit) ? 1 : 0.1,
          transition: 'opacity 300ms ease-out',
        }}
      >
        {/* Nothing here returns you to a berth. Once the ship is out, the way
            back into a dock is to fly into one — a button that teleported you
            there made the whole capture volume decorative. So this is only ever
            the way back to wherever the ship actually is.

            No starmap button either: the dock opens the map from its own
            console and flight has the chart. Offering it a second time from the
            corner meant two controls for one thing. */}
        {mode !== 'flight' && flight.current && (
          <button type="button" onClick={() => setMode('flight')} style={tabStyle(false)}>
            back to ship
          </button>
        )}
        {mode !== 'docked' && mode !== 'flight' && !flight.current && (
          <button type="button" onClick={() => setMode('docked')} style={tabStyle(false)}>
            back to dock
          </button>
        )}

        {/* Re-centring the chart is chart chrome, but it belongs in this row
            rather than in a second one of its own — the map had grown its own
            stack of buttons in the same corner, offering a duplicate way back
            to the ship alongside this one. Cluster level, because the useful
            question after wandering is where you sit relative to your
            neighbours, not what is in orbit at home. */}
        {chart && (
          <>
            <button
              type="button"
              onClick={() => focusSystem(systemId, 'cluster')}
              style={tabStyle(false)}
            >
              ◎ current system
            </button>
            {/* The way out. It lived on the chart itself until the chart's own
                corner menu was folded into this row, and went with it — which
                left the map with no exit at all. */}
            <button type="button" onClick={() => setChart(false)} style={tabStyle(false)}>
              ← close chart{SHOW_KEY_HINTS ? ' (esc)' : ''}
            </button>
          </>
        )}
        {/* The menu, which is what this always said and now finally is.
            It used to go straight to the main menu — a button labelled "menu"
            that meant "leave what you are doing" — and returning to the main
            menu is now a choice inside it rather than the whole of it. */}
        <button type="button" onClick={() => setMenuOpen(true)} style={tabStyle(false)}>
          menu{SHOW_KEY_HINTS ? ' (esc)' : ''}
        </button>
        <span style={{ padding: '5px 12px', opacity: 0.5, fontSize: 11 }}>
          {player ? player.username : describe(auth)}
        </span>
      </div>
      )}
    </>
  );
}

function describe(auth: AuthState): string {
  switch (auth.status) {
    case 'loading':
      return 'signing in…';
    case 'signedIn':
      return 'signed in';
    case 'signedOut':
      return 'not signed in';
    case 'error':
      return `sign-in failed: ${auth.message}`;
  }
}

const tabStyle = (active: boolean): React.CSSProperties => ({
  background: active ? 'rgba(127,212,255,0.22)' : 'transparent',
  border: 'none',
  borderRight: '1px solid rgba(127,212,255,0.2)',
  color: active ? '#dbeaff' : 'rgba(219,234,255,0.7)',
  font: 'inherit',
  fontSize: 12,
  padding: '5px 12px',
  cursor: 'pointer',
});

const switcherStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 16,
  right: 16,
  display: 'flex',
  alignItems: 'center',
  background: 'rgba(8, 14, 24, 0.82)',
  border: '1px solid rgba(127, 212, 255, 0.22)',
};

/**
 * The last thing the server refused, said out loud.
 *
 * Transient rather than dismissible: a refusal is information about a thing that
 * did not happen, and asking somebody to close it is asking them to acknowledge
 * a non-event. It fades on its own, and a new one replaces it rather than
 * queueing — the newest refusal is the one that just happened.
 *
 * Centred at the top, clear of the ship console down the left and the radio down
 * the right, and above everything: a message about an action that failed has to
 * be readable from whichever view the action was taken in.
 */
function Refusal({ text, onDone }: { text: string; onDone: () => void }) {
  useEffect(() => {
    const timer = window.setTimeout(onDone, 4200);
    return () => window.clearTimeout(timer);
    // Keyed on the text so a second refusal restarts the clock rather than
    // inheriting whatever was left of the first one's.
  }, [text, onDone]);

  return (
    <div
      style={{
        position: 'absolute',
        top: 14,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 80,
        maxWidth: 'min(560px, 70vw)',
        textAlign: 'center',
        background: 'rgba(24,8,4,0.92)',
        border: '1px solid rgba(255,95,107,0.55)',
        color: '#ffb3b8',
        padding: '8px 14px',
        fontSize: 12,
        lineHeight: 1.45,
        pointerEvents: 'none',
      }}
    >
      {text}
    </div>
  );
}
